// Where Guess talks to: the real server (recognition, secret answers, history) or, in the static
// preview build, a local backend (bundled sample pictures, the browser's recogniser, local judge).

import type {
  GuessCategoryInfo,
  GuessMatchInfo,
  GuessPlayerIn,
  GuessResult,
  SttLang,
  UtteranceResponse,
  VoiceTestInfo,
} from "@shared/guess/api";
import type { ClockEvent } from "@shared/guess/clock";
import type { Lang } from "@shared/types";
import { api } from "../../lib/api";
import type { Utterance } from "./audio/listener";

export interface GuessConfig {
  stt: { mode: "server" | "browser"; provider: string; langs: SttLang[] };
  samplesAvailable: boolean;
  sampleDays: number;
}

export interface VoiceAttemptResult {
  wordOk: boolean;
  heard: string;
  error?: string;
  test: VoiceTestInfo;
}

export interface GuessBackend {
  kind: "server" | "local";
  config(): Promise<GuessConfig>;
  categories(lang: Lang): Promise<GuessCategoryInfo[]>;
  createMatch(input: { category: string; lang: Lang; players: GuessPlayerIn[]; ownerIndex: number | null }): Promise<GuessMatchInfo>;
  utterance(matchId: string, u: Utterance, player: number, picture: number): Promise<UtteranceResponse>;
  finish(matchId: string, events: ClockEvent[]): Promise<{ result: GuessResult; verified: boolean }>;
  abort(matchId: string): Promise<void>;
  voiceTest(lang: "ka" | "en", keepSamples: boolean): Promise<VoiceTestInfo>;
  voiceAttempt(testId: string, word: number, u: Utterance, calibration?: { noiseDb: number; speechDb: number; avgMs: number } | null): Promise<VoiceAttemptResult>;
}

// ------------------------------------------------------------------ server

const serverBackend: GuessBackend = {
  kind: "server",
  async config() {
    const r = await api<GuessConfig & { ok: true }>("/guess/config");
    return { stt: r.stt, samplesAvailable: r.samplesAvailable, sampleDays: r.sampleDays };
  },
  async categories(lang) {
    return (await api<{ categories: GuessCategoryInfo[] }>(`/guess/categories?lang=${lang}`)).categories;
  },
  async createMatch(input) {
    return (await api<{ match: GuessMatchInfo }>("/guess/matches", { body: input })).match;
  },
  async utterance(matchId, u, player, picture) {
    const q = `u=${encodeURIComponent(u.id)}&p=${player}&i=${picture}&ms=${Math.round(u.speechMs)}`;
    const path = `/guess/matches/${matchId}/utterances?${q}`;
    if (u.wav) return api<UtteranceResponse>(path, { raw: u.wav, timeoutMs: 15_000 });
    return api<UtteranceResponse>(path, { body: { transcripts: u.transcripts ?? [] }, timeoutMs: 15_000 });
  },
  async finish(matchId, events) {
    const r = await api<{ result: GuessResult; verified: boolean }>(`/guess/matches/${matchId}/finish`, { body: { events }, timeoutMs: 20_000 });
    return { result: r.result, verified: r.verified };
  },
  async abort(matchId) {
    await api(`/guess/matches/${matchId}/abort`, { method: "POST", body: {} }).catch(() => undefined);
  },
  async voiceTest(lang, keepSamples) {
    return (await api<{ test: VoiceTestInfo }>("/guess/voice-tests", { body: { lang, keepSamples } })).test;
  },
  async voiceAttempt(testId, word, u, cal) {
    const q = new URLSearchParams({ w: String(word), u: u.id });
    if (cal) {
      q.set("n", String(cal.noiseDb));
      q.set("s", String(cal.speechDb));
      q.set("d", String(cal.avgMs));
    }
    const path = `/guess/voice-tests/${testId}/attempts?${q}`;
    const r = u.wav
      ? await api<VoiceAttemptResult>(path, { raw: u.wav, timeoutMs: 15_000 })
      : await api<VoiceAttemptResult>(path, { body: { transcripts: u.transcripts ?? [] }, timeoutMs: 15_000 });
    return { wordOk: r.wordOk, heard: r.heard, error: r.error, test: r.test };
  },
};

// ------------------------------------------------------------------ local (static preview only)

export const LOCAL_HISTORY_KEY = "hg:guess:history";

/** The preview backend lives in its own chunk; production builds drop it entirely (and its answers). */
function lazyLocal(): GuessBackend {
  const m = () => import("./backend-local").then((x) => x.localBackend);
  return {
    kind: "local",
    config: () => m().then((b) => b.config()),
    categories: (lang) => m().then((b) => b.categories(lang)),
    createMatch: (input) => m().then((b) => b.createMatch(input)),
    utterance: (id, u, p, i) => m().then((b) => b.utterance(id, u, p, i)),
    finish: (id, ev) => m().then((b) => b.finish(id, ev)),
    abort: (id) => m().then((b) => b.abort(id)),
    voiceTest: (lang, keep) => m().then((b) => b.voiceTest(lang, keep)),
    voiceAttempt: (id, w, u, c) => m().then((b) => b.voiceAttempt(id, w, u, c)),
  };
}

export const guessBackend: GuessBackend = __DEMO__ ? lazyLocal() : serverBackend;
